import { Button } from '@heidi/design-system/button';
import { TypographyFootNote, TypographyP2Bold } from '@heidi/design-system/typography';
import Image from 'next/image';
import type { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import sessionMergeIllustration from '../../assets/session-merge-illustration.jpg';
import { useSessionMergeOnboardingUiHost } from '../../host';

export type EnableSessionMergeCardProps = {
  /** "Turn on" — enables auto session merge. */
  onTryNow: () => void;
  /** Close (X) — dismisses the card without enabling. */
  onDismiss: () => void;
  /** Disables the CTA while the enable request is in flight. */
  isEnabling?: boolean;
};

/**
 * "Enable Session Merge" awareness card (Figma 8240-30589): the shared
 * {@link AwarenessCard} populated with the session-merge illustration, copy, and
 * a single "Turn on" CTA. The floating popover shell owns queue state, analytics,
 * and the enable action.
 */
export function EnableSessionMergeCard({
  onTryNow,
  onDismiss,
  isEnabling,
}: EnableSessionMergeCardProps): ReactElement {
  const { AwarenessCard } = useSessionMergeOnboardingUiHost();
  return (
    <AwarenessCard
      onClose={onDismiss}
      image={
        <div className="relative h-[120px] w-full">
          <Image
            src={sessionMergeIllustration}
            alt=""
            aria-hidden
            fill
            className="object-cover"
          />
        </div>
      }
      title={
        <TypographyP2Bold className="text-text-primary">
          <FormattedMessage defaultMessage="Enable Session Merge" id="cnzkkmrvwF" />
        </TypographyP2Bold>
      }
      actions={
        <Button
          type="button"
          variant="secondary"
          size="xs"
          className="w-full"
          onClick={onTryNow}
          disabled={isEnabling}
        >
          <FormattedMessage defaultMessage="Turn on" id="npvxprjeDK" />
        </Button>
      }
    >
      <TypographyFootNote className="text-wrap text-text-secondary">
        <FormattedMessage
          defaultMessage="Heidi can now automatically merge sessions for the same patient, creating a more complete note."
          id="lem8QBcKbi"
        />
      </TypographyFootNote>
    </AwarenessCard>
  );
}
